<template>
  <Modal ref="modalRef" @after-close="clearForm">
    <template #header>Add a phone number</template>

    <template #body>
      <FormGroup class="form">
        <Text variant="body-medium" color="secondary">
          Tallo gives renters the number of whoever covers their tour, so {{ firstName }} needs one before you can pick
          them.
        </Text>

        <FormField :label="`${fullName}'s phone number`" :error="form.fieldErrors('phone')">
          <InputPhone v-model="form.fields.phone" />
        </FormField>
      </FormGroup>
    </template>

    <template #footer>
      <Button variant="ghost" @click="modalRef?.close">Cancel</Button>
      <Button @click="save" :loading="form.submitting">Save</Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { useQueryCache } from '@pinia/colada';
import { helpers, required } from '@vuelidate/validators';
import { AxiosError } from 'axios';
import { computed, ref } from 'vue';

import {
  Button,
  FormField,
  FormGroup,
  InputPhone,
  Modal,
  Text,
  phoneNumberValidator,
  useAlertsStore,
  useForm,
} from '@tallo/design-system';
import { getFullName } from '@tallo/utility';

import { ShowingAgentDto } from '../interfaces/showing-agent.dto';
import { showingAgentsQueryKey } from '../queries/showing-agents.query';
import showingAgentsService from '../services/showing-agents.service';

const emit = defineEmits<{ saved: [showingAgent: ShowingAgentDto] }>();

const queryCache = useQueryCache();
const alertsStore = useAlertsStore();
const modalRef = ref<InstanceType<typeof Modal>>();
const showingAgent = ref<ShowingAgentDto>();
// A team member's copy has no last name when their account name is one word, so the join leaves a trailing space
const fullName = computed(() => (showingAgent.value ? getFullName(showingAgent.value).trim() : ''));
const firstName = computed(() => showingAgent.value?.firstName || fullName.value);

const form = useForm<{ phone: string }>(
  { phone: '' },
  {
    phone: {
      phoneNumberValidator,
      required: helpers.withMessage('Phone number is required', required),
    },
  },
);

function open(agent: ShowingAgentDto) {
  showingAgent.value = agent;
  modalRef.value?.show();
}

async function save() {
  const agent = showingAgent.value;

  if (!agent) {
    return;
  }

  try {
    await form.submit(async ({ phone }) => {
      await showingAgentsService.update(agent.id, { phone });
      await queryCache.invalidateQueries({ key: [showingAgentsQueryKey] });

      modalRef.value?.close();
      emit('saved', { ...agent, phone });
    });
  } catch (error) {
    // A rejected field lands on the field itself; anything else from the server is a toast
    if (error instanceof AxiosError && !error.response?.data?.validationErrors) {
      alertsStore.catchError(error);
    }
  }
}

function clearForm() {
  form.fields.phone = '';
  form.resetValidation();
}

defineExpose({ open });
</script>

<style scoped lang="scss">
.form {
  width: 27rem;
  max-width: 100%;
}
</style>
